export const MOVE_EMIT_MIN_MS = 100;
export const INTERP_MS = 100;
export const INTERP_DELAY_MS = 150;
export const EXTRAPOLATE_MS = 80;
export const VIEW_EMIT_MIN_MS = 250;
export const VIEW_MARGIN = 256;
export const STALE_INTERP_MS = 250;
const MAX_POSES = 8;
const CLOCK_OFFSET_SMOOTH = 0.15;

const MOVE_STATES = ['idle', 'walk', 'run'];

export function moveStateToInt(moveState) {
  const i = MOVE_STATES.indexOf(moveState);
  return i >= 0 ? i : 1;
}

export function intToMoveState(value) {
  return MOVE_STATES[value] || 'walk';
}

/**
 * Whether the client should emit a move packet.
 * Sit/stand teleports pass immediate. Stopping always flushes an idle packet.
 */
export function shouldEmitMove({
  now,
  lastEmitAt,
  moveState,
  lastSentMoveState,
  immediate
}) {
  if (immediate) return true;
  if (moveState === 'idle' && lastSentMoveState && lastSentMoveState !== 'idle') {
    return true;
  }
  if (lastEmitAt == null) return true;
  return (now - lastEmitAt) >= MOVE_EMIT_MIN_MS;
}

export function lerp(a, b, t) {
  return a + (b - a) * t;
}

/**
 * Map server Date.now() into client clock space. EMA so a late packet
 * does not yank the whole buffer.
 */
export function applyClockOffset(prevOffset, serverT, clientNow) {
  if (!Number.isFinite(serverT) || !Number.isFinite(clientNow)) return prevOffset;
  const sample = clientNow - serverT;
  if (prevOffset == null || !Number.isFinite(prevOffset)) return sample;
  return prevOffset + (sample - prevOffset) * CLOCK_OFFSET_SMOOTH;
}

function poseTime(packed, now, clockOffset) {
  const serverT = packed.t != null ? packed.t : now;
  return serverT + (clockOffset || 0);
}

/**
 * Fold a packed snapshot pose into a timestamped buffer.
 * Teleports (tp) and stale re-entry snap immediately — used for sit/stand.
 */
export function applySnapshotPose(prevInterp, packed, now, clockOffset = 0) {
  const moveState = intToMoveState(packed.s);
  const t = poseTime(packed, now, clockOffset);
  const pose = { x: packed.x, y: packed.y, moveState, t };
  if (packed.tp) {
    return { poses: [{ ...pose, t: now }], snapped: true };
  }
  const prevPoses = prevInterp && prevInterp.poses ? prevInterp.poses : [];
  const last = prevPoses[prevPoses.length - 1];
  const gap = last ? t - last.t : Infinity;
  const stale = !last || gap > STALE_INTERP_MS;
  if (stale) {
    return { poses: [{ ...pose, t: now }], snapped: true };
  }
  const poses = prevPoses.concat(pose);
  while (poses.length > MAX_POSES) poses.shift();
  return { poses, snapped: false };
}

function sampleBetween(a, b, renderT, latestMoveState) {
  const span = b.t - a.t;
  const u = span > 0 ? Math.max(0, Math.min(1, (renderT - a.t) / span)) : 1;
  let moveState = b.moveState === 'idle'
    ? 'idle'
    : (u < 1 && a.moveState !== 'idle' ? a.moveState : b.moveState);
  // Stop the walk cycle as soon as we know they idled, without waiting
  // for the delayed render time to reach that pose.
  if (latestMoveState === 'idle') moveState = 'idle';
  return {
    x: lerp(a.x, b.x, u),
    y: lerp(a.y, b.y, u),
    moveState
  };
}

/**
 * Sample a pose buffer at a render time 1.5 ticks in the past so a late
 * snapshot still arrives before we run out of path. Moving poses coast
 * briefly past the last sample so setInterval jitter does not hitch.
 */
export function sampleInterp(interp, now) {
  if (!interp || !interp.poses || !interp.poses.length) return null;
  const poses = interp.poses;
  const latest = poses[poses.length - 1];

  if (interp.snapped) {
    return { x: latest.x, y: latest.y, moveState: latest.moveState };
  }

  const renderT = now - INTERP_DELAY_MS;
  if (renderT <= poses[0].t) {
    return {
      x: poses[0].x,
      y: poses[0].y,
      moveState: latest.moveState === 'idle' ? 'idle' : poses[0].moveState
    };
  }

  for (let i = 0; i < poses.length - 1; i++) {
    const a = poses[i];
    const b = poses[i + 1];
    if (renderT <= b.t) {
      return sampleBetween(a, b, renderT, latest.moveState);
    }
  }

  const overdue = renderT - latest.t;
  if (latest.moveState !== 'idle' && overdue >= INTERP_DELAY_MS) {
    return { x: latest.x, y: latest.y, moveState: 'idle' };
  }

  const prev = poses.length >= 2 ? poses[poses.length - 2] : null;
  if (
    prev
    && latest.moveState !== 'idle'
    && overdue > 0
    && overdue <= EXTRAPOLATE_MS
  ) {
    const span = latest.t - prev.t;
    if (span > 0) {
      const u = overdue / span;
      return {
        x: latest.x + (latest.x - prev.x) * u,
        y: latest.y + (latest.y - prev.y) * u,
        moveState: latest.moveState
      };
    }
  }

  return { x: latest.x, y: latest.y, moveState: latest.moveState };
}

export function viewUnchanged(prev, next) {
  if (!prev || !next) return false;
  return prev.x === next.x
    && prev.y === next.y
    && prev.w === next.w
    && prev.h === next.h;
}
